import { useState } from 'react';
import useSpectatorGame from '../hooks/useSpectatorGame';
import { useAuthContext } from '../contexts/AuthContext';
import AuthModal from './modals/AuthModal';
import DiceLogo from './DiceLogo';

export default function SpectatorView({ code, onLeave }) {
  const {
    players, currentPlayerIndex, turnNumber, gamePhotoUrl, status, winner,
    loading, error, redemption,
    joinState, matchedSlot, claimSlot, requestToJoin,
  } = useSpectatorGame(code);
  const { user, linkedProfile } = useAuthContext();
  const [acting, setActing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const isFinished = status === 'finished';

  async function handleClaim() {
    setActing(true);
    await claimSlot();
    setActing(false);
  }

  async function handleRequest() {
    // Anonymous users get prompted to sign in OR can request as guest with a name.
    let displayName = linkedProfile?.name || user?.displayName;
    if (!displayName) {
      displayName = window.prompt("What's your name?");
      if (!displayName) return;
    }
    setActing(true);
    await requestToJoin(displayName);
    setActing(false);
  }

  if (loading) {
    return (
      <div id="spectator-view">
        <DiceLogo />
        <div className="spectator-loading">joining game...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div id="spectator-view">
        <DiceLogo />
        <p className="spectator-error">{error}</p>
        <button className="btn-bank" onClick={onLeave}>Back</button>
      </div>
    );
  }

  const currentPlayer = players[currentPlayerIndex];
  const sortedPlayers = [...players].sort((a, b) => b.score - a.score);

  return (
    <div id="spectator-view">
      {gamePhotoUrl && (
        <img className="game-icon-photo" src={gamePhotoUrl} alt="Game selfie" />
      )}

      <div className="spectator-header">
        <div className="spectator-badge">LIVE</div>
        <div className="spectator-code">{code}</div>
      </div>

      {/* === Join state UI === */}
      {!isFinished && joinState === 'matched' && (
        <button
          className="btn-bank spectator-join-btn"
          onClick={handleClaim}
          disabled={acting}
        >
          {acting ? 'Joining...' : `🎯 Claim your slot — ${matchedSlot.name}`}
        </button>
      )}

      {!isFinished && joinState === 'open' && (
        <>
          <button
            className="btn-bank spectator-join-btn"
            onClick={handleRequest}
            disabled={acting}
          >
            {acting ? 'Asking...' : 'Ask to join'}
          </button>
          {user?.isAnonymous && (
            <p className="spectator-signin-hint">
              <button className="btn-link" onClick={() => setShowAuthModal(true)}>
                Sign in
              </button>{' '}
              to keep your stats.
            </p>
          )}
        </>
      )}

      {!isFinished && joinState === 'pending' && (
        <div className="spectator-joined-badge">⏳ Waiting for the host to accept...</div>
      )}

      {!isFinished && joinState === 'declined' && (
        <div className="spectator-declined-badge">
          The host didn't accept your request — you can still watch.
        </div>
      )}

      {!isFinished && joinState === 'joined' && (
        <div className="spectator-joined-badge">You&rsquo;re in the game!</div>
      )}

      {isFinished ? (
        <div className="spectator-status">
          <h2 className="spectator-winner-title">{winner?.name || 'Someone'} wins!</h2>
        </div>
      ) : (
        <div className="spectator-status">
          <div className="spectator-turn-info">
            <div className="avatar">{currentPlayer?.avatar}</div>
            <span className="spectator-turn-name">
              {currentPlayer?.name}'s Turn
            </span>
          </div>
          <div className="spectator-turn-number">
            {redemption ? `Redemption · Beat ${redemption.currentTarget.toLocaleString()}` : `Turn ${turnNumber}`}
          </div>
        </div>
      )}

      <div className="spectator-leaderboard">
        {sortedPlayers.map((player, index) => {
          const rank = index + 1;
          const isActive = !isFinished && player.id === currentPlayer?.id;
          const isMe = (user && player.authUid === user.uid) || (linkedProfile?.id === player.id);
          const isPlayerWinner = isFinished && player.id === winner?.id;

          return (
            <div
              key={player.id}
              className={`spectator-player-card ${isActive ? 'spectator-active' : ''} ${isPlayerWinner ? 'spectator-winner' : ''}`}
            >
              <div className={`rank ${rank === 1 ? 'rank-1' : ''}`}>{rank}</div>
              <div className="avatar">{player.avatar}</div>
              <div className="spectator-player-info">
                <div className="spectator-player-name">
                  {player.name}
                  {isMe && <span className="spectator-you-badge">you</span>}
                </div>
                <div className="spectator-player-score">{player.score.toLocaleString()}</div>
              </div>
              <div className="spectator-last-turn">{player.lastTurn}</div>
            </div>
          );
        })}
      </div>

      <div className="spectator-footer">
        <button className="btn-quit" onClick={onLeave}>Leave Game</button>
      </div>

      <AuthModal show={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
